import type { TranslatedFormField } from '../../measuringPoint/types';
import type {
    ElementSettings,
    ElementSettingsFormField,
    ElementSettingsFormSchema,
} from '../elements/types';
import {
    normalizeFieldDefault,
    normalizeFieldForSave,
} from './settingsFormSchemaRuntime/normalization';
import { assertSettingsFormSchemaMetadata } from './fieldSettingsGuards';

interface CompiledSettingsFormFieldRuntime {
    field: ElementSettingsFormField;
    toLayoutField(): TranslatedFormField;
    toDefaultValue(currentSettings: ElementSettings): unknown;
    applyValidatedValue(
        nextSettings: ElementSettings,
        currentSettings: ElementSettings,
        validatedValue: unknown
    ): void;
}

export interface CompiledSettingsFormSchemaRuntime {
    fields: readonly CompiledSettingsFormFieldRuntime[];
    createDefaultValues(currentSettings: ElementSettings): Record<string, unknown>;
    applyValidatedValues(
        currentSettings: ElementSettings,
        validatedValues: Record<string, unknown>
    ): ElementSettings;
}

function createFieldRuntime(field: ElementSettingsFormField): CompiledSettingsFormFieldRuntime {
    return {
        field,
        toLayoutField(): TranslatedFormField {
            return { ...field } as TranslatedFormField;
        },
        toDefaultValue(currentSettings: ElementSettings): unknown {
            const rawValue = currentSettings[field.name];
            const sourceValue = typeof rawValue === 'undefined' ? field.default : rawValue;
            return normalizeFieldDefault(field, sourceValue);
        },
        applyValidatedValue(
            nextSettings: ElementSettings,
            currentSettings: ElementSettings,
            validatedValue: unknown
        ): void {
            if (field.type === 'definitions' && typeof validatedValue === 'undefined') {
                return;
            }

            const normalizedFieldValue = normalizeFieldForSave(field, validatedValue);

            if (typeof normalizedFieldValue === 'undefined') {
                delete nextSettings[field.name];
                return;
            }

            nextSettings[field.name] = normalizedFieldValue;
        },
    };
}

export function compileSettingsFormSchemaRuntime(
    settingsFormSchema: ElementSettingsFormSchema
): CompiledSettingsFormSchemaRuntime {
    // Compile boundary validates developer-authored form schema metadata before
    // field layout/default/save runtime paths execute.
    assertSettingsFormSchemaMetadata(settingsFormSchema);

    const fieldRuntimes = settingsFormSchema.fields.map((field) => createFieldRuntime(field));
    const runtime: CompiledSettingsFormSchemaRuntime = {
        fields: fieldRuntimes,
        createDefaultValues(currentSettings: ElementSettings): Record<string, unknown> {
            return Object.fromEntries(
                fieldRuntimes.map((fieldRuntime) => [
                    fieldRuntime.field.name,
                    fieldRuntime.toDefaultValue(currentSettings),
                ])
            );
        },
        applyValidatedValues(
            currentSettings: ElementSettings,
            validatedValues: Record<string, unknown>
        ): ElementSettings {
            const nextSettings: ElementSettings = { ...currentSettings };

            fieldRuntimes.forEach((fieldRuntime) => {
                fieldRuntime.applyValidatedValue(
                    nextSettings,
                    currentSettings,
                    validatedValues[fieldRuntime.field.name]
                );
            });

            return nextSettings;
        },
    };

    return runtime;
}
